import { ConfirmationDialog } from "common/styleguide/ConfirmationDialog";
import { commonMessages, commonNouns, commonVerbs, fm } from "lib/localization";
import _ from "lodash";
import React, { Fragment } from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";

export const ARCHIVE_ROUTES_DIALOG = "ARCHIVE_ROUTES_DIALOG";
export const UNARCHIVE_ROUTES_DIALOG = "UNARCHIVE_ROUTES_DIALOG";

class ArchiveDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      pending: false,
      error: false,
    };
  }

  onConfirm = () => {
    const { onConfirm, onRequestClose } = this.props;
    this.setState({ pending: true });
    onConfirm()
      .then(onRequestClose)
      .catch(() => this.setState({ pending: false, error: true }));
  };

  render() {
    const { onRequestClose, confirmMessage, errorMessage, confirmText, title } = this.props;
    const { pending, error } = this.state;

    return (
      <ConfirmationDialog
        primary
        danger
        maxWidth="600px"
        onRequestClose={onRequestClose}
        onCancel={onRequestClose}
        onConfirm={this.onConfirm}
        confirmPending={pending}
        confirmText={confirmText}
        title={title}
      >
        {confirmMessage}
        {error && <div className={errorMessage}>{errorMessage}</div>}
      </ConfirmationDialog>
    );
  }
}

const messages = defineMessages({
  confirmText: {
    id: "ArchiveRoutes.confirmText",
    defaultMessage: "{action} {str}",
  },
  delay: {
    id: "ArchiveRoutes.delay",
    defaultMessage: "Note: This will take up to a minute to process.",
  },
  theseRoutes: {
    id: "ArchiveRoutes.theseRoutes",
    defaultMessage: "these routes",
  },
  thisRoute: {
    id: "ArchiveRoutes.thisRoute",
    defaultMessage: "this route",
  },
  title: {
    id: "ArchiveRoutes.title",
    defaultMessage: "{action} {str}",
    values: { str: (...str) => <strong>{str}</strong> },
  },
});

const toggleArchiveRouteComponent =
  (isArchive) =>
  ({ route, intl, ...props }) => {
    const action = _.capitalize(fm(intl, isArchive ? commonVerbs.archive : commonVerbs.unarchive));
    return (
      <ArchiveDialog
        title={fm(intl, messages.title, { action, str: route.name })}
        confirmText={fm(intl, messages.confirmText, {
          action,
          str: fm(intl, commonNouns.routes, { count: 1 }),
        })}
        confirmMessage={
          <Fragment>
            <p>
              <T
                id="ArchiveRoute.singleConfirmMsg"
                defaultMessage="Are you sure you want to {action} your route, {name}?"
                values={{ action, name: <strong>{route.name}</strong> }}
              />
            </p>
            <p>{fm(intl, messages.delay)}</p>
          </Fragment>
        }
        errorMessage={fm(intl, commonMessages.errorMessage, {
          action,
          operand: fm(intl, messages.thisRoute),
        })}
        {...props}
      />
    );
  };

const ArchiveRoute = toggleArchiveRouteComponent(true);
const UnarchiveRoute = toggleArchiveRouteComponent(false);

const toggleArchiveRoutesComponent =
  (isArchive) =>
  ({ routes, intl, ...props }) => {
    if (routes.length === 1) {
      const Component = isArchive ? ArchiveRoute : UnarchiveRoute;
      return <Component intl={intl} route={routes[0]} {...props} />;
    } else {
      const action = _.capitalize(fm(intl, isArchive ? commonVerbs.archive : commonVerbs.unarchive));
      const countStr = `${routes.length} ${fm(intl, commonNouns.routes, {
        count: routes.length,
      })}`;

      return (
        <ArchiveDialog
          title={fm(intl, messages.title, { action, str: countStr })}
          confirmMessage={
            <Fragment>
              <p>
                <T
                  id="ArchiveRoute.multiConfirmMsg"
                  defaultMessage="Are you sure you want to {action} {str}?"
                  values={{ action, str: <strong>{countStr}</strong> }}
                />
              </p>
              <p>{fm(intl, messages.delay)}</p>
            </Fragment>
          }
          confirmText={fm(intl, messages.confirmText, {
            action,
            str: countStr,
          })}
          errorMessage={fm(intl, commonMessages.errorMessage, {
            action,
            operand: fm(intl, messages.theseRoutes),
          })}
          {...props}
        />
      );
    }
  };

export const ArchiveRoutes = injectIntl(toggleArchiveRoutesComponent(true));
export const UnarchiveRoutes = injectIntl(toggleArchiveRoutesComponent(false));
